import { useCallback, useEffect, useRef, useState, JSX } from 'react';
import { useRecordContext } from 'react-admin';
import { Alert, Box, Chip, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { eyebrow, numeric } from '../../../dashboard/components/styles/dashboard-tokens';
import { Operator } from '../../../../types/entities';
import { loadOperatorQueue, OperatorQueueState } from './operatorQueueApi';

/**
 * The Queue tab on the Operator edit screen, Stage 6 of the HQ queue screens.
 *
 * Everything here comes from the Operator's own `GET /system/queue/`, which Stage 5 built. The HQ
 * Queues screen at `#/system-queues` is the other half of the picture and reads HQ-API's Doctrine
 * transports; **the two never overlap.** RabbitMQ is the Operator's business alone and HQ-API
 * publishes nothing to it, so a reading here says nothing about that screen and the other way
 * round.
 *
 * No permission check in the component. Reaching this tab at all needs `HQ_System::operators.view`,
 * which react-admin has already enforced on the Edit screen, and the Operator enforces its own
 * ticket. A third check here would be the same rule written a third time.
 */

/** How stale a reading is allowed to get while the tab is being watched. */
const POLL_MILLISECONDS: number = 15000;

/**
 * How often to ask whether a poll is due.
 *
 * Shorter than the poll itself so that switching to this tab refreshes it promptly instead of
 * waiting out the remainder of a fifteen second interval. The tick itself only reads `offsetParent`
 * when nothing is due, which costs nothing.
 */
const TICK_MILLISECONDS: number = 5000;

const panel = {
    p: 2.5,
    borderRadius: 2,
    border: 1,
    borderColor: 'divider',
    bgcolor: 'background.paper',
} as const;

/**
 * One number, or an em dash when it could not be taken.
 *
 * The dash is the point: a count that could not be read is not a queue of zero, and rendering it as
 * `0` would report a healthy empty queue on an Operator that has lost its broker. Same treatment as
 * `HqApiQueuesPage`, deliberately, so the two screens read alike.
 */
const Figure = ({ value, label }: { value: number | null, label: string }): JSX.Element => (
    <Stack spacing={ 0.25 } sx={ { minWidth: 96 } }>
        <Typography sx={ { ...numeric, fontSize: 22, lineHeight: 1.1 } }>
            { value === null ? '—' : value }
        </Typography>
        <Typography sx={ eyebrow }>{ label }</Typography>
    </Stack>
);

/** How long the job in hand has been running, in the shortest form that is still honest. */
const describeRunningFor = (seconds: number | null): string => {
    if (seconds === null) {
        return 'for an unknown time';
    }
    if (seconds < 60) {
        return 'for ' + seconds + 's';
    }
    if (seconds < 3600) {
        return 'for ' + Math.floor(seconds / 60) + 'm';
    }

    return 'for ' + Math.floor(seconds / 3600) + 'h';
};

/**
 * What this Operator is doing, as one chip.
 *
 * Draining is checked first because it is the state that explains the other two: a draining
 * Operator is still connected and has stopped consuming on purpose, and calling that "not
 * consuming" would send someone looking for a fault.
 */
const ConsumerChip = ({ state }: { state: OperatorQueueState }): JSX.Element => {
    if (state.draining) {
        return <Chip size="small" color="warning" label="Shutting down" />;
    }
    if (state.consuming) {
        return <Chip size="small" color="success" label="Consuming" />;
    }
    if (state.connected) {
        // Connected to the broker with no consumer attached. Stop does exactly this, so it is an
        // ordinary state rather than a fault, and it is not the same as having no connection.
        return <Chip size="small" color="warning" label="Connected, not consuming" />;
    }

    return <Chip size="small" color="error" label="Not connected" />;
};

export const OperatorQueue: () => JSX.Element = (): JSX.Element => {
    const record: Operator = useRecordContext();
    const [state, setState] = useState<OperatorQueueState | null>(null);
    const [problem, setProblem] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    // The panel's own root, used only to ask whether this tab is the open one. See the poll below.
    const root = useRef<HTMLDivElement | null>(null);
    const lastReadAt = useRef<number>(0);
    const wasVisible = useRef<boolean>(false);

    const operatorId: number | string | undefined = record?.id;

    const read = useCallback(async (): Promise<void> => {
        if (operatorId === undefined) {
            return;
        }
        try {
            setState(await loadOperatorQueue(operatorId));
            setProblem('');
        } catch (unreachable: any) {
            // **The last good reading is cleared.** Leaving it on screen beside an error would
            // show a depth that is no longer known to be true, which is the one thing this panel
            // must not do.
            setState(null);
            setProblem(unreachable?.message ?? 'The Operator could not be reached.');
        } finally {
            setLoading(false);
        }
    }, [operatorId]);

    useEffect((): (() => void) => {
        // Read once on mount even though the tab is probably not the open one: react-admin mounts
        // every tab and hides the inactive ones, so this is what makes the panel populated the
        // instant someone opens it.
        void read();
        lastReadAt.current = Date.now();
        wasVisible.current = false;

        /**
         * **Polled only while the tab is actually on screen.**
         *
         * `TabbedForm` renders a hidden tab with `display: none` rather than unmounting it, so a
         * plain interval here would go on minting a ticket and calling the Operator every fifteen
         * seconds while the admin sat on the Details tab, for a panel nobody was looking at.
         * `offsetParent` is null under `display: none`, which is the cheapest reliable way to ask,
         * and it does not care where this tab sits in the order - a check on the tab index in the
         * URL would stop working the moment someone reordered them.
         */
        const timer: ReturnType<typeof setInterval> = setInterval((): void => {
            const visible: boolean = root.current?.offsetParent != null;
            if (!visible) {
                wasVisible.current = false;

                return;
            }

            // Refresh straight away on becoming visible, rather than showing a reading that could
            // be a quarter of a minute old while the next tick is waited out.
            const due: boolean = !wasVisible.current || Date.now() - lastReadAt.current >= POLL_MILLISECONDS;
            wasVisible.current = true;
            if (due) {
                lastReadAt.current = Date.now();
                void read();
            }
        }, TICK_MILLISECONDS);

        return (): void => clearInterval(timer);
    }, [read]);

    return (
        <Box ref={ root } sx={ { width: '100%' } }>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={ { mb: 1.5 } }>
                <Box>
                    <Typography sx={ eyebrow }>{ 'Work queue' }</Typography>
                    <Typography variant="body2" color="text.secondary">
                        { 'Read from this Operator directly. HQ-API\'s own queues are on the Queues screen.' }
                    </Typography>
                </Box>
                <IconButton title="Refresh queue" onClick={ (): void => { void read(); } }>
                    <Refresh />
                </IconButton>
            </Stack>

            { loading && <LinearProgress /> }

            { problem !== '' &&
                <Alert severity="error" sx={ { mb: 2 } }>{ problem }</Alert>
            }

            { state !== null &&
                <Box sx={ panel }>
                    <Stack direction="row" alignItems="center" spacing={ 2 } flexWrap="wrap" useFlexGap sx={ { mb: 2 } }>
                        <Typography sx={ { ...numeric, fontWeight: 600, minWidth: 140 } }>{ state.queue }</Typography>
                        <ConsumerChip state={ state } />
                        { !state.depthRead &&
                            <Chip size="small" color="default" variant="outlined" label="Depth unavailable" />
                        }
                    </Stack>

                    <Stack direction="row" spacing={ 4 } flexWrap="wrap" useFlexGap>
                        <Figure value={ state.depth } label="Waiting" />
                        <Figure value={ state.consumers } label="Consumers" />
                    </Stack>

                    <Box sx={ { pt: 2, mt: 2, borderTop: 1, borderColor: 'divider' } }>
                        <Typography sx={ eyebrow }>{ 'In flight' }</Typography>
                        { state.inFlight === null &&
                            <Typography variant="body2" color="text.secondary">
                                { state.consuming ? 'Idle, waiting for work.' : 'Nothing in hand.' }
                            </Typography>
                        }
                        { state.inFlight !== null &&
                            <Stack direction="row" alignItems="baseline" spacing={ 2 } flexWrap="wrap" useFlexGap>
                                <Typography sx={ { ...numeric, fontWeight: 600, wordBreak: 'break-all' } }>
                                    { state.inFlight.job }
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    { 'Running ' + describeRunningFor(state.inFlight.runningForSeconds)
                                      + (state.inFlight.attempt === null ? '' : ', attempt ' + state.inFlight.attempt) }
                                </Typography>
                            </Stack>
                        }
                    </Box>

                    { !state.depthRead && state.connected &&
                        <Alert severity="warning" sx={ { mt: 2 } }>
                            { 'The Operator is running but could not read the queue from the broker. '
                              + 'The counts above are unknown, not zero.' }
                        </Alert>
                    }
                </Box>
            }
        </Box>
    );
};
